import React, { useEffect, useRef, useState } from "react";
import { EmailInput } from "../../../components/EmailInput";
import { DateInput } from "../../../components/DateInput";
import { isBefore } from "../../../functional/dateUtils";
import { isValidEmail } from "../../../functional/isValidEmail";
import styled from "styled-components";
import { Checkbox } from "../../../components/Checkbox";
import { withURLParam, removeURLParams } from "../utils/paramUtils";
import { formatShortLocale } from "../utils/formatLocale";
import { usePageMetadata } from "../../../context/PageMetadata";
import { AnalyticsEventTypes, sendEvent } from "../../../functional/analyticsUtils";
import { useAnalytics } from "../../../providers/AnalyticsProvider";
import { CTA } from "../../../components/CTA";
export const EmailForm = styled.div ``;
const SUCCESS_PARAM_KEY = "submit";
const SUCCESS_PARAM_VALUE = "success";
const ERROR_PARAM_KEY = "errMsg";
const _CaptureForm = ({ className, minAge, publicationList, externalKey = "RiotAccountData", fragmentId, textStrings, }) => {
    const { locale } = usePageMetadata();
    const languagePreference = formatShortLocale(locale);
    const formRef = useRef(null);
    const formStateProcessed = useRef(false);
    const emailInputRef = useRef(null);
    const [birthdate, setBirthdate] = useState("");
    const [email, setEmail] = useState("");
    const [agreement, setAgreement] = useState(false);
    const [success, setSuccess] = useState(false);
    const [successURL, setSuccessURL] = useState("");
    const [errorURL, setErrorURL] = useState("");
    const [submitError, setSubmitError] = useState(false);
    const errorString = "An%20error%20occurred%20updating%20the%20data%20extension";
    const formInvalid = !agreement || !isValidEmail(email) || birthdate === "";
    const minDate = new Date();
    minDate.setFullYear(minDate.getFullYear() - minAge);
    const earliestBirthdate = minDate.toISOString().split("T")[0];
    const analyticsContext = useAnalytics();
    useEffect(() => {
        if (formStateProcessed.current === true) {
            return;
        }
        const currentURL = new URL(location.href);
        const isSuccess = currentURL.searchParams.get(SUCCESS_PARAM_KEY) === SUCCESS_PARAM_VALUE;
        const isError = currentURL.searchParams.has(ERROR_PARAM_KEY);
        const baseURL = removeURLParams(currentURL, [SUCCESS_PARAM_KEY, ERROR_PARAM_KEY]);
        if (isSuccess || isError) {
            baseURL.hash = fragmentId || "";
            history.replaceState(null, "", baseURL.toString());
        }
        baseURL.hash = "";
        setSuccessURL(withURLParam(baseURL, SUCCESS_PARAM_KEY, SUCCESS_PARAM_VALUE).toString());
        setErrorURL(baseURL.toString());
        if (isSuccess) {
            setSuccess(true);
            sendEvent(AnalyticsEventTypes.email_subscribe_success, { ...analyticsContext });
        }
        if (isError) {
            setSubmitError(true);
            sendEvent(AnalyticsEventTypes.email_subscribe_error, { ...analyticsContext });
        }
        formStateProcessed.current = true;
    }, [analyticsContext, fragmentId]);
    const emailSubscribeStartEventSent = useRef(false);
    const redirectToErrorPage = () => {
        const updatedURL = withURLParam(location.href, "errMsg", errorString, fragmentId);
        location.href = updatedURL.toString();
    };
    const handleCTASubmitClick = () => {
        if (!formInvalid) {
            if (isBefore(earliestBirthdate, birthdate)) {
                redirectToErrorPage();
                return;
            }
            formRef.current?.submit();
        }
    };
    const handleSubmit = (e) => {
        if (isBefore(earliestBirthdate, birthdate)) {
            e.preventDefault();
            redirectToErrorPage();
        }
    };
    const sendEmailSubscribeStartEvent = () => {
        if (emailSubscribeStartEventSent.current) {
            return;
        }
        sendEvent(AnalyticsEventTypes.email_subscribe_start, { ...analyticsContext });
        emailSubscribeStartEventSent.current = true;
    };
    const handleEmailChange = (value) => {
        setEmail(value);
        sendEmailSubscribeStartEvent();
    };
    const handleBirthdateChange = (value) => {
        setBirthdate(value);
        sendEmailSubscribeStartEvent();
    };
    return (React.createElement(EmailForm, { id: "signup" },
        React.createElement("form", { className: `${className} ${success ? "hide" : "show"}`, action: "https://cl.s10.exct.net/DEManager.aspx", method: "post", encType: "application/x-www-form-urlencoded", "data-testid": "email-form", onSubmit: handleSubmit, ref: formRef },
            React.createElement("input", { type: "hidden", name: "_clientID", value: "100025126" }),
            React.createElement("input", { type: "hidden", name: "_deExternalKey", value: externalKey }),
            React.createElement("input", { type: "hidden", name: "_action", value: "add/update" }),
            React.createElement("input", { type: "hidden", name: "_returnXML", value: "1" }),
            React.createElement("input", { type: "hidden", name: "_successURL", value: successURL }),
            React.createElement("input", { type: "hidden", name: "_errorURL", value: errorURL }),
            React.createElement("input", { type: "hidden", name: "puuid", value: email }),
            React.createElement("input", { type: "hidden", name: publicationList, value: "true" }),
            React.createElement("input", { type: "hidden", name: "language preference", value: languagePreference }),
            React.createElement("label", { className: "input-label", htmlFor: "email" }, textStrings.emailLabel),
            React.createElement(EmailInput, { title: "email", name: "Email Address", onValueChange: handleEmailChange, value: email, ref: emailInputRef, placeholder: textStrings.emailPlaceholder }),
            React.createElement("div", { className: "dob-container" },
                React.createElement("label", { className: "input-label", htmlFor: "dob" }, textStrings.dobLabel),
                React.createElement(DateInput, { name: "Birth Date", id: "dob", onValueChange: handleBirthdateChange, value: birthdate })),
            React.createElement("div", { className: "agreement" },
                React.createElement(Checkbox, { className: "checkbox", id: "checkbox", name: "opt_in", onCheckedChange: setAgreement, checked: agreement, value: "true", "aria-labelledby": "consent-text" }),
                React.createElement("label", { id: "consent-text", className: "terms", htmlFor: "checkbox", "data-testid": "checkbox-label" }, textStrings.consentText)),
            React.createElement("div", { className: "submission" },
                React.createElement(CTA, { variant: "secondary", role: "button", "data-testid": "submit-button", disabled: formInvalid, className: "submit", onClick: handleCTASubmitClick }, textStrings.submitText),
                submitError && (React.createElement("div", { className: "error", "data-testid": "error-text" }, textStrings.dobErrorText)))),
        React.createElement("div", { className: `successMessage ${success ? "show" : "hide"}`, "data-testid": "success-message" }, textStrings.successText)));
};
_CaptureForm.defaultProps = {
    "data-testid": "email-capture-form",
};
export const CaptureForm = styled(_CaptureForm) ``;
//# sourceMappingURL=CaptureForm.js.map